import hoistNonReactStatics from 'hoist-non-react-statics';
import React, { Component } from 'react';
import memoize from 'lodash/memoize';

const THEMR_CONFIG = '__THEMR_CONFIG__';

const DEFAULT_OPTIONS = {
  composeTheme: 'deeply',
  mapThemrProps: defaultMapThemrProps,
};

function defaultMapThemrProps(ownProps, theme) {
  const {
    composeTheme,
    themeNamespace,
    mapThemrProps,
    ...rest
  } = ownProps;

  return {
    ...rest,
    theme,
  };
}

const uniqueSymbol = Symbol('someuniquekey');

function isProxy(obj) {
  return Boolean(obj) && obj[uniqueSymbol] === uniqueSymbol;
}

function isComponentLike(value) {
  if (!value) return false;
  if (typeof value === 'function') return true;
  return typeof value === 'object' && Boolean(value.$$typeof);
}

function getDisplayName(ComponentLike) {
  return ComponentLike.displayName || ComponentLike.name || 'Component';
}

function merge(original = {}, mixin = {}) {
  if (process.env.NODE_ENV !== 'production') {
    if (isProxy(original)) return original;
    if (isProxy(mixin)) return mixin;
  }

  const result = { ...original };

  Object.keys(mixin).forEach(key => {
    const originalValue = original[key];
    const mixinValue = mixin[key];

    if (typeof originalValue === 'function' || typeof mixinValue === 'function') {
      return;
    }

    if (mixinValue === undefined) return;

    if (originalValue === undefined) {
      result[key] = mixinValue;
      return;
    }

    if (typeof mixinValue === 'object' && typeof originalValue === 'object') {
      result[key] = merge(originalValue, mixinValue);
      return;
    }

    if (typeof mixinValue === 'object' || typeof originalValue === 'object') {
      throw new Error(
        `You are merging non-object with object, original: ${originalValue}, mixin: ${mixinValue}, key: ${key}`
      );
    }

    result[key] = originalValue
      .split(' ')
      .concat(mixinValue.split(' '))
      .filter((item, pos, self) => self.indexOf(item) === pos && item !== '')
      .join(' ');
  });

  return result;
}

export function themeable(...themes) {
  return themes.reduce((acc, theme) => merge(acc, theme), {});
}

const createThemedComponent = (componentName, localTheme, options = {}, ThemedComponent) => {
  const {
    composeTheme: optionComposeTheme,
    mapThemrProps: optionMapThemrProps,
  } = { ...DEFAULT_OPTIONS, ...options };

  const wrappedDisplayName = getDisplayName(ThemedComponent);

  const componentConfig = ThemedComponent[THEMR_CONFIG];
  if (componentConfig && componentConfig.componentName === componentName) {
    componentConfig.localTheme = themeable(componentConfig.localTheme, localTheme);
    return ThemedComponent;
  }

  const config = { componentName, localTheme };

  const getNamespacedTheme = ({ theme, themeNamespace }) => {
    if (!themeNamespace) return theme;
    if (themeNamespace && !theme) {
      throw new Error(
        'Invalid themeNamespace use in react-css-themr. themeNamespace prop should be used only with theme prop.'
      );
    }

    return Object.keys(theme)
      .filter(key => key.startsWith(themeNamespace))
      .reduce((result, key) => {
        const capitalized = key.substr(themeNamespace.length);
        const renamedKey = capitalized.charAt(0).toLowerCase() + capitalized.slice(1);
        result[renamedKey] = theme[key];
        return result;
      }, {});
  };

  const getTheme = ({ theme, composeTheme, themeNamespace }, contextTheme) => {
    return composeTheme === 'softly'
      ? {
          ...contextTheme,
          ...config.localTheme,
          ...getNamespacedTheme({ themeNamespace, theme }),
        }
      : themeable(
          themeable(contextTheme, config.localTheme),
          getNamespacedTheme({ themeNamespace, theme })
        );
  };

  const getThemeNotComposed = ({ theme, themeNamespace }, contextTheme) => {
    if (theme) return getNamespacedTheme({ theme, themeNamespace });
    if (config.localTheme) return config.localTheme;
    return contextTheme;
  };

  const calcTheme = memoize((theme, composeTheme, themeNamespace, contextTheme) =>
    composeTheme
      ? getTheme({ theme, composeTheme, themeNamespace }, contextTheme)
      : getThemeNotComposed({ theme, themeNamespace }, contextTheme)
  );

  class Themed extends Component {
    static displayName = `Themed${wrappedDisplayName}`;
    static defaultProps = {
      ...ThemedComponent.defaultProps,
      composeTheme: optionComposeTheme,
      mapThemrProps: optionMapThemrProps,
    };

    static [THEMR_CONFIG] = config;

    getContextTheme() {
      return this.context && this.context.themr
        ? this.context.themr.theme[config.componentName]
        : {};
    }

    render() {
      const { theme, composeTheme, themeNamespace, forwardRef, mapThemrProps } = this.props;
      const calculatedTheme = calcTheme(theme, composeTheme, themeNamespace, this.getContextTheme());

      return React.createElement(ThemedComponent, {
        ...mapThemrProps(this.props, calculatedTheme),
        ref: forwardRef,
      });
    }
  }

  const NewComponent = hoistNonReactStatics(Themed, ThemedComponent);

  return React.forwardRef((props, ref) => {
    return <NewComponent {...props} forwardRef={ref} />;
  });
};

export const themr = (componentNameOrComponent, localTheme, options = {}) => {
  if (isComponentLike(componentNameOrComponent)) {
    return createThemedComponent(
      getDisplayName(componentNameOrComponent),
      localTheme,
      options,
      componentNameOrComponent
    );
  }

  return ThemedComponent => createThemedComponent(componentNameOrComponent, localTheme, options, ThemedComponent);
};